import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (!productGrid) {
      if (currentUrl && !this.#abortController?.signal.aborted) window.location.assign(currentUrl);
      return;
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;


    const source = productGrid.querySelector('.product-details');
    const sourceTitle = source?.querySelector('.view-product-title a');
    const titleText = sourceTitle?.textContent?.trim() || this.dataset.productTitle || '';
    const normalize = (value) => value.normalize('NFKC').toLocaleLowerCase().replace(/[\s\p{P}]+/gu, '');
    const titleKey = normalize(titleText);
    const french = (document.documentElement.lang || 'fr').toLowerCase().startsWith('fr');
    const price = source?.querySelector('product-price');
    const picker = source?.querySelector('variant-picker');
    const form = source?.querySelector('product-form-component');
    const purchase = form?.closest('.buy-buttons-block') || form;
    const imageSources = [...productGrid.querySelectorAll('.product-information__media .product-media > img')];
    const layout = document.createElement('div');
    layout.className = 'bb-quick-view';
    const body = document.createElement('div');
    body.className = 'bb-quick-view__body';

    if (imageSources.length) {
      const media = document.createElement('bb-product-gallery');
      media.className = 'bb-quick-view__media';
      const track = document.createElement('div');
      track.className = 'bb-quick-view__track';
      track.tabIndex = 0;
      track.setAttribute('role', 'region');
      track.setAttribute('aria-label', french ? 'Photos du produit' : 'Product photos');
      const seen = new Set();
      for (const original of imageSources) {
        const key = original.closest('[data-media-id]')?.getAttribute('data-media-id') || original.getAttribute('src');
        if (seen.has(key)) continue;
        seen.add(key);
        const figure = document.createElement('figure');
        figure.className = 'bb-quick-view__slide';
        const image = original.cloneNode(true);
        for (const attr of ['id', 'style', 'class', 'ref', 'fetchpriority']) image.removeAttribute(attr);
        image.setAttribute('loading', seen.size === 1 ? 'eager' : 'lazy');
        image.setAttribute('decoding', 'async');
        image.setAttribute('sizes', '(min-width: 750px) 460px, calc(100vw - 40px)');
        if (!image.getAttribute('alt')) image.setAttribute('alt', `${titleText} — ${seen.size}`);
        figure.appendChild(image);
        track.appendChild(figure);
      }
      media.appendChild(track);
      if (seen.size > 1) {
        const controls = document.createElement('div');
        controls.className = 'bb-quick-view__gallery-controls';
        for (const direction of [-1, 1]) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.direction = String(direction);
          button.textContent = direction === -1 ? '←' : '→';
          button.setAttribute('aria-label', french
            ? (direction === -1 ? 'Photo précédente' : 'Photo suivante')
            : (direction === -1 ? 'Previous photo' : 'Next photo'));
          controls.appendChild(button);
        }
        const hint = document.createElement('span');
        hint.textContent = french ? `${seen.size} photos · faites défiler` : `${seen.size} photos · swipe to explore`;
        controls.insertBefore(hint, controls.lastChild);
        media.appendChild(controls);
      }
      layout.appendChild(media);
    } else {
      layout.classList.add('bb-quick-view--no-image');
    }

    const heading = document.createElement('h2');
    heading.className = 'bb-quick-view__title';
    heading.id = 'bb-quick-view-title';
    heading.textContent = titleText;
    body.appendChild(heading);
    if (price) body.appendChild(price);

    const description = document.createElement('div');
    description.className = 'bb-quick-view__description';
    const candidates = Array.from(source?.querySelectorAll('.text-block') || []);
    for (const block of candidates) {
      if (block.closest('product-price, product-form-component, variant-picker, .view-product-title, [ref="productTitleLink"], product-recommendations, .custom-liquid')) continue;
      if (block.parentElement?.closest('.text-block')) continue;
      const text = block.textContent?.trim() || '';
      if (!text || normalize(text) === titleKey) continue;
      const copy = block.cloneNode(true);
      copy.removeAttribute('id');
      copy.removeAttribute('style');
      copy.removeAttribute('class');
      copy.removeAttribute('data-shopify-editor-block');
      copy.className = 'bb-quick-view__text';
      for (const duplicate of copy.querySelectorAll('h1, h2, h3, [role="heading"]')) {
        if (normalize(duplicate.textContent || '') === titleKey) duplicate.remove();
      }
      if (copy.textContent?.trim()) description.appendChild(copy);
    }

    const lead = description.querySelector('p');
    if (lead && lead.textContent?.trim()) {
      const intro = document.createElement('div');
      intro.className = 'bb-quick-view__intro';
      intro.appendChild(lead);
      body.appendChild(intro);
    }
    if (picker) body.appendChild(picker);
    if (purchase) {
      const actions = document.createElement('div');
      actions.className = 'bb-quick-view__purchase';
      actions.appendChild(purchase);
      body.appendChild(actions);
    }

    if (description.textContent?.trim()) {
      const details = document.createElement('details');
      details.className = 'bb-quick-view__details';
      const summary = document.createElement('summary');
      summary.textContent = french ? 'Description et contenu' : 'Description and contents';
      details.append(summary, description);
      body.appendChild(details);
    }
    const fullPage = document.createElement('a');
    fullPage.className = 'bb-quick-view__link';
    fullPage.href = sourceTitle?.getAttribute('href') || this.productPageUrl;
    fullPage.textContent = french ? 'Voir la fiche complète' : 'View full product details';
    body.appendChild(fullPage);
    layout.appendChild(body);
    productGrid.replaceChildren(layout);
    const dialog = modalContent.closest('dialog');
    dialog?.setAttribute('aria-labelledby', heading.id);

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    modalContent.scrollTop = 0;
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.bb-quick-view__link'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

// Native horizontal scrolling: no gallery library or duplicated product request.
class BBProductGallery extends HTMLElement {
  #controller;
  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-direction]');
      const track = this.querySelector('.bb-quick-view__track');
      if (!button || !track) return;
      track.scrollBy({
        left: Number(button.dataset.direction) * track.clientWidth,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }, { signal: this.#controller.signal });
  }
  disconnectedCallback() {
    this.#controller?.abort();
  }
}
if (!customElements.get('bb-product-gallery')) customElements.define('bb-product-gallery', BBProductGallery);
